// @flow

import { featureIsEnabled } from '@ta/common.features';
import type { ResponsiveImageProps } from '../ResponsiveImage';
import type { FitStrategy, Source } from '../types';
import getDevicePixelRatio from './getDevicePixelRatio';
import getDynamicSource from './getDynamicSource';

/** helper struct that contains data used by getSource */
type SourceWithUsageInfo = {|
  src: Source,
  stretchRatio: number,
  wastedRatio: number,
|};

/** The default percent we allow an image to be stretched before bumping up to a higher resolution */
const DEFAULT_ALLOWED_STRETCH = 0.25;

/** The max source image width we allow in pixels. If the target image is larger than this we step down in size. */
const MAX_SOURCE_IMAGE_WIDTH = 2560;

/** Images with fewer total pixels than this number will always be accepted */
const WHITELISTED_TOTAL_PIXELS = 40000;

/**
 * Choose the most appropriate image to display.
 * First, try to use ResponsiveImage, which will take care of this for you.
 * This method is provided for cases when it doesn't make sense to use ResponsiveImage.
 *
 * This method will find the size that does not exceed the requirements for being stretched, and also has
 * the smallest amount of wasted pixels.
 */
const getSource = (
  clientWidth: number,
  clientHeight: number,
  sources: $PropertyType<ResponsiveImageProps, 'sources'>,
  options?: {
    allowedStretch?: $PropertyType<ResponsiveImageProps, 'allowedStretch'>,
    getImageBasedOnClientInfo?: boolean,
    fitStrategy?: FitStrategy,
  },
): Source => {
  if (!sources || sources.length === 0) {
    console.error('ResponsiveImage sources was empty');
    return {
      url: '',
      width: 0,
      height: 0,
    };
  }

  const allowedStretch = options?.allowedStretch || DEFAULT_ALLOWED_STRETCH;
  const fitStrategy = options?.fitStrategy || 'cover';
  const getImageBasedOnClientInfo =
    options && options.getImageBasedOnClientInfo !== undefined
      ? options.getImageBasedOnClientInfo
      : true;

  const devicePixelRatio = getDevicePixelRatio(getImageBasedOnClientInfo);
  const targetImgWidth = clientWidth * devicePixelRatio;
  const targetImgHeight = clientHeight * devicePixelRatio;
  const targetImgPx = targetImgHeight * targetImgWidth;

  const dynamicSize = sources.find(src => src.width === 0 && src.height === 0);
  if (dynamicSize && featureIsEnabled('media_responsive_image_use_dynamic_size')) {
    return getDynamicSource(
      targetImgWidth,
      targetImgHeight,
      sources,
      dynamicSize,
      allowedStretch,
      fitStrategy,
    );
  }

  // Do not allow images larger than 'MAX_SOURCE_IMAGE_WIDTH' pixels wide
  const sourcesFiltered = sources.filter(src => src.width <= MAX_SOURCE_IMAGE_WIDTH);
  if (sourcesFiltered.length === 0) {
    // use the last source because it will be the largest.  The first one could be a dynamic cut, which could have width and height set to zero
    const lastSource = sources[sources.length - 1];
    const missingWidthHeight = sources.length > 0 && (!lastSource.width || !lastSource.height);
    console.error(
      `ResponsiveImage: no suitable source found. sources.length ${
        sources.length
      } missingWidthHeight ${missingWidthHeight.toString()}`,
    );
    return {
      url: '',
      width: 0,
      height: 0,
    };
  }

  // For each image, calculate the stretch and the waste
  const sourcesWithInfo: SourceWithUsageInfo[] = sourcesFiltered.map(src => {
    const widthStretchRatio = (targetImgWidth - src.width) / src.width;
    const heightStretchRatio = (targetImgHeight - src.height) / src.height;
    const stretchRatio = Math.max(widthStretchRatio, heightStretchRatio);

    const srcPx = src.width * src.height;
    const wastedRatio = (srcPx - targetImgPx) / srcPx;
    return { src, stretchRatio, wastedRatio };
  });

  // sort by wastedRatio
  sourcesWithInfo.sort((a, b) => a.wastedRatio - b.wastedRatio);

  // If there is an image that is small and does not require stretching, just use it.
  // We are more concerned with quality than file size here because the files are not very big
  const smallAndNoStretch = sourcesWithInfo
    .filter(s => s.src.width * s.src.height <= WHITELISTED_TOTAL_PIXELS)
    .filter(s => s.stretchRatio <= 0);
  if (smallAndNoStretch.length > 0) {
    // Choose the one with the least amount of waste
    const { url, width, height } = smallAndNoStretch[0].src;
    return { url, width, height };
  }

  // filter for maxStretch
  const stretchIsAcceptable = sourcesWithInfo.filter(s => s.stretchRatio <= allowedStretch);
  if (stretchIsAcceptable.length > 0) {
    // Choose the one with the least amount of waste
    const { url, width, height } = stretchIsAcceptable[0].src;
    return { url, width, height };
  }

  // All of the available cuts need to be stretched more than the acceptable amount
  // Pick the one that needs to be stretched the least
  sourcesWithInfo.sort((a, b) => a.stretchRatio - b.stretchRatio);
  const { url, width, height } = sourcesWithInfo[0].src;
  return { url, width, height };
};

export default getSource;
